import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import RevenueCalculator from "@/pages/RevenueCalculator";
import ListingSampleCarousel from "@/pages/ListingSampleCarousel";
import {
  ArrowDown,
  ArrowRight,
  Check,
  ChevronRight,
  Sparkles,
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  Play,
  ChevronLeft,
  TrendingDown,
  AlertTriangle,
  Compass,
  ArrowUpRight,
  X,
} from "lucide-react";

const reels = [
  {
    id: 0,
    videoId: "1204001173",
    aspect: "vertical",
    title: "BRoll Lifestyle Reel",
    thumb: "https://vumbnail.com/1204001173.jpg",
    duration: "",
    views: "194K",
  },
  {
    id: 1,
    videoId: "1213412294",
    aspect: "vertical",
    title: "Hype Listing Reel",
    thumb: "https://vumbnail.com/1213412294.jpg",
    duration: "",
    views: "158K",
  },
  {
    id: 2,
    videoId: "1200213203",
    aspect: "vertical",
    title: "Broll Lifestyle Reel",
    thumb: "https://vumbnail.com/1200213203.jpg",
    duration: "",
    views: "16.5K",
  },
  {
    id: 3,
    videoId: "1201955703",
    aspect: "vertical",
    title: "Talking Tour",
    thumb: "https://vumbnail.com/1201955703.jpg",
    duration: "",
    views: "135K",
  },
];

const offerItems = [
  "1 strategy & check-in call per month (30–60 min)",
  "4 content shoots per month — on-site in Bend, OR send us raw footage if you're remote",
  "All content created for you — Reels, carousels, the works. Quality over volume.",
  "Up to 20 Reels + 4 Carousels per month · 24 posts total",
  "Tech stack & automations managed end-to-end — post-CTA triggers, DM flows, lead capture",
];

const whoItsFor = [
  "High-producing agents who are too busy to make content themselves",
  "Agents earning serious money who want the result — without the learning curve",
  "Current Content LAB or 1:1 coaching clients ready to fully hand it off",
];

const milestones = [
  {
    id: 0,
    label: "The Beginning",
    body:
      "You were posting. Showing up. But nothing was landing. No traction. No pipeline. Just noise into the void.",
    status: "past",
  },
  {
    id: 1,
    label: "Content LAB",
    body:
      "We built your strategy together — what to post, how to post it, and how to turn it into a system that actually feeds your pipeline.",
    status: "past",
  },
  {
    id: 2,
    label: "Today",
    body:
      "You've got the playbook. The momentum. And a decision to make. Because what happens next is what separates the agents who stay on top from the ones who quietly fade.",
    status: "now",
  },
];

const paths = [
  {
    id: "coast",
    icon: TrendingDown,
    badge: "Path 01",
    title: "Coast.",
    body:
      "Fall off the wagon. Skip a week. Then two. The old habits creep back, the feed goes quiet — and so does your phone.",
    tone: "muted",
    delay: 0,
  },
  {
    id: "outsource",
    icon: AlertTriangle,
    badge: "Path 02",
    title: "Outsource to a stranger.",
    body:
      "Hire a generic videographer or social media manager. They check boxes. Nothing converts. Your brand and your pipeline drift backward.",
    tone: "muted",
    delay: 0.12,
  },
  {
    id: "1on1",
    icon: Compass,
    badge: "Path 03",
    title: "Keep working with me 1:1.",
    body:
      "You build the brand yourself — with monthly coaching, accountability, and a strategist who knows your market and your style.",
    tone: "good",
    delay: 0.35,
  },
  {
    id: "contenthaus",
    icon: ArrowUpRight,
    badge: "Path 04 · The next chapter",
    title: "Hand it off. ContentHaus.",
    body:
      "We capture, edit and post everything for the next 6–12 months. You stay consistent on autopilot — and focus on closing deals.",
    tone: "featured",
    delay: 0.5,
  },
];

const pathReveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};
const pathTransition = (delay) => ({
  duration: 0.65,
  ease: [0.22, 1, 0.36, 1],
  delay,
});

const Stat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-3.5 h-3.5 text-neutral-500" strokeWidth={2} aria-hidden="true" />
    <span className="font-helvetica font-bold text-[0.7rem] tracking-[0.18em] uppercase text-neutral-900">{value}</span>
    <span className="font-helvetica text-[0.65rem] tracking-[0.22em] uppercase text-neutral-400">{label}</span>
  </div>
);

const StatCompact = ({ icon: Icon, value, label }) => (
  <div className="flex items-start gap-1.5 min-w-0">
    <Icon className="w-3 h-3 mt-[3px] text-neutral-500 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
    <div className="flex flex-col min-w-0">
      <span className="font-helvetica font-bold text-[0.7rem] leading-tight tracking-[0.05em] text-neutral-900 truncate">{value}</span>
      <span className="font-helvetica text-[0.55rem] tracking-[0.2em] uppercase text-neutral-400 truncate">{label}</span>
    </div>
  </div>
);

const ReelCard = ({ reel }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div data-testid={`reel-card-${reel.id}`} className="group">
      <div className="relative overflow-hidden rounded-[1.25rem] bg-neutral-100 aspect-[9/16]">
        {playing ? (
          <>
            <iframe
              src={`https://player.vimeo.com/video/${reel.videoId}?autoplay=1&loop=1&badge=0&autopause=0&player_id=0&app_id=58479`}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              title={reel.title}
            />
            <button
              type="button"
              data-testid={`reel-close-${reel.id}`}
              onClick={() => setPlaying(false)}
              aria-label="Close video"
              className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.25} aria-hidden="true" />
            </button>
          </>
        ) : (
          <button
            type="button"
            data-testid={`reel-play-${reel.id}`}
            onClick={() => setPlaying(true)}
            className="absolute inset-0 block w-full h-full text-left focus:outline-none"
          >
            <img
              src={reel.thumb}
              alt={reel.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
            <span className="absolute top-4 left-4 flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1">
              <Play className="w-2.5 h-2.5 text-neutral-950 fill-neutral-950" aria-hidden="true" />
              <span className="font-helvetica font-bold text-[0.6rem] tracking-[0.22em] uppercase text-neutral-950">Reel</span>
            </span>
            <span className="absolute top-4 right-4 bg-black/55 backdrop-blur-sm rounded-full px-2.5 py-1" style={{ display: reel.duration ? "block" : "none" }}>
              <span className="font-helvetica font-bold text-[0.65rem] tracking-wider text-white">{reel.duration}</span>
            </span>
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] transition-all duration-300 group-hover:scale-110 group-hover:bg-white">
                <Play className="w-5 h-5 text-neutral-950 fill-neutral-950 ml-0.5" aria-hidden="true" />
              </span>
            </span>
            <span className="absolute bottom-5 left-5 right-5 block">
              <span className="font-akira uppercase tracking-tight text-white text-lg leading-[1.05] block">{reel.title}</span>
            </span>
          </button>
        )}
      </div>
      <div className="mt-5 flex items-baseline gap-3">
        <Play className="w-4 h-4 text-neutral-500 self-center" strokeWidth={2} aria-hidden="true" />
        <span className="font-akira uppercase text-2xl md:text-3xl text-neutral-900 tracking-tight leading-none">{reel.views}</span>
        <span className="font-helvetica font-bold text-[0.6rem] tracking-[0.3em] uppercase text-neutral-400">Views</span>
      </div>
    </div>
  );
};

const PricingCard = ({ plan, featured }) => (
  <div
    data-testid={`pricing-card-${plan.id}`}
    className={`relative flex flex-col text-center rounded-[2rem] p-10 md:p-12 transition-all duration-500 ${
      featured
        ? "bg-neutral-950 text-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.5)] border border-neutral-900"
        : "bg-white text-neutral-900 border border-neutral-200 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_20px_60px_-30px_rgba(0,0,0,0.12)]"
    }`}
  >
    {featured && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-white text-neutral-950 text-[0.6rem] font-helvetica font-bold uppercase tracking-[0.28em] px-3 py-1.5 rounded-full">
        <Sparkles className="w-3 h-3" strokeWidth={2.5} aria-hidden="true" />
        Best Value · Save $5,400
      </span>
    )}
    <div className={`font-helvetica font-bold text-sm tracking-[0.3em] uppercase ${featured ? "text-neutral-400" : "text-neutral-500"}`}>
      {plan.term}
    </div>
    <div className="my-8 md:my-10">
      <div className="flex items-start justify-center gap-1">
        <span className={`font-helvetica font-bold text-2xl md:text-3xl leading-none mt-3 ${featured ? "text-neutral-400" : "text-neutral-400"}`}>$</span>
        <span className={`font-helvetica font-bold leading-none tracking-tight text-6xl md:text-[6rem] ${featured ? "text-white" : "text-neutral-900"}`}>
          {plan.monthly}
        </span>
      </div>
      <div className={`mt-3 font-advercase text-sm ${featured ? "text-neutral-400" : "text-neutral-500"}`}>
        per month · {plan.term.toLowerCase()}
      </div>
    </div>
    <div className={`font-advercase text-base leading-relaxed px-2 ${featured ? "text-neutral-300" : "text-neutral-600"}`}>
      {plan.tagline}
    </div>

    {/* Primary CTA — Enroll (monthly Stripe checkout) */}
    <a
      data-testid={`pricing-cta-${plan.id}`}
      href={plan.monthlyLink}
      target="_blank"
      rel="noopener noreferrer"
      className={`mt-8 w-full inline-flex items-center justify-center py-4 rounded-full font-helvetica font-bold text-xs tracking-[0.28em] uppercase transition-all duration-300 bg-neutral-950 text-white hover:bg-neutral-800 ${
        featured ? "ring-1 ring-white/20 hover:ring-white/30" : ""
      }`}
    >
      Enroll for {plan.term}
    </a>

    {/* Secondary CTA — Pay in Full */}
    <a
      data-testid={`pricing-cta-pif-${plan.id}`}
      href={plan.pifLink}
      target="_blank"
      rel="noopener noreferrer"
      className={`mt-3 w-full inline-flex items-center justify-center py-3.5 rounded-full font-helvetica font-bold text-xs tracking-[0.28em] uppercase transition-all duration-300 bg-white text-neutral-950 hover:bg-neutral-100 ${
        featured ? "" : "border border-neutral-300"
      }`}
    >
      Pay in full · ${plan.pifPrice.toLocaleString()}
      <span className="ml-2 font-helvetica text-[0.6rem] tracking-[0.2em] text-neutral-500">
        Save ${plan.savings.toLocaleString()}
      </span>
    </a>

    <div
      className={`mt-5 font-advercase text-[0.7rem] tracking-wide ${
        featured ? "text-neutral-500" : "text-neutral-400"
      }`}
    >
      Secure Stripe checkout · Cancel anytime
    </div>
  </div>
);

const plans = [
  {
    id: "6-months",
    term: "6 Months",
    monthly: "3,500",
    pifPrice: 19000,
    savings: 2000,
    tagline: "Hand it off for two full quarters. Build a content engine that compounds while you close.",
    monthlyLink: "https://buy.stripe.com/5kQ7sL0cR2d98f1e5Bfw40O",
    pifLink: "https://buy.stripe.com/00w4gz7Fj2d90Mzd1xfw40S",
  },
  {
    id: "12-months",
    term: "12 Months",
    monthly: "3,200",
    pifPrice: 36000,
    savings: 2400,
    tagline: "A full year of consistent, compounding content — the best path to becoming your market's most recognizable agent.",
    monthlyLink: "https://buy.stripe.com/aFafZh3p3cRN0Mz7Hdfw40T",
    pifLink: "https://buy.stripe.com/00wdR90cRaJF1QD8Lhfw40U",
  },
];

const ContentHaus = () => {
  const [, setHovered] = useState(null);

  // Timeline scroll-linked draw animation
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start 80%", "end 55%"],
  });
  const lineScaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const node2Opacity = useTransform(scrollYProgress, [0.35, 0.55], [0, 1]);
  const node3Opacity = useTransform(scrollYProgress, [0.85, 1], [0, 1]);
  const node3Scale = useTransform(scrollYProgress, [0.85, 1], [0.6, 1]);

  return (
    <div data-testid="contenthaus-page" className="min-h-screen bg-[#FAFAFA] text-neutral-900">
      <main>
        {/* HERO */}
        <section data-testid="hero-section" className="relative pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
            <p data-testid="hero-eyebrow" className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
              ContentHaus · by Vertical Video Pro
            </p>
            <h1
              data-testid="hero-title"
              className="font-akira uppercase tracking-tight leading-[0.95] text-neutral-900 text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[6rem]"
            >
              We build your <br />
              local brand.<br />
              <span className="text-neutral-400">You close the deals.</span>
            </h1>
            <p data-testid="hero-subtitle" className="mt-8 max-w-2xl mx-auto font-advercase text-base md:text-lg text-neutral-600 leading-relaxed">
              The same transformation as our 1:1 mentorship — executed for you, not taught to you.
              A full content team capturing, editing and posting the work that builds
              your pipeline, so you can focus on what you do best: serving your clients.
            </p>
            <div className="mt-10 flex flex-col items-center gap-5">
              <a
                href="#examples"
                data-testid="hero-cta-see-examples"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-neutral-950 text-white font-helvetica font-bold text-xs tracking-[0.25em] uppercase hover:bg-neutral-800 transition-all duration-300"
              >
                See our content
                <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
              </a>
              <span data-testid="hero-note" className="font-advercase text-xs md:text-sm text-neutral-400 tracking-wide">
                By application only · A few non-competing agents per market.
              </span>
            </div>
          </div>
        </section>

        {/* JOURNEY / TIMELINE */}
        <section data-testid="journey-section" className="py-20 md:py-28 border-t border-neutral-200 bg-white">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16 md:mb-24">
              <p data-testid="journey-eyebrow" className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
                The Journey
              </p>
              <h2 data-testid="journey-title" className="font-akira uppercase tracking-tight text-neutral-900 text-3xl md:text-5xl leading-[0.95]">
                From posting into the void.<br />
                <span className="text-neutral-400">To owning your market.</span>
              </h2>
            </div>

            {/* Timeline */}
            <div data-testid="timeline" ref={timelineRef} className="relative mb-20 md:mb-28">
              {/* desktop connector line (track) */}
              <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-[2px] bg-neutral-200" />
              {/* desktop connector line (animated fill) */}
              <motion.div
                aria-hidden="true"
                className="hidden md:block absolute top-7 left-[12%] right-[12%] h-[2px] bg-neutral-950 origin-left"
                style={{ scaleX: lineScaleX }}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative">
                {milestones.map((m, i) => {
                  const isNow = m.status === "now";
                  const dotMotionStyle = i === 1
                    ? { opacity: node2Opacity }
                    : i === 2
                    ? { opacity: node3Opacity, scale: node3Scale }
                    : undefined;
                  return (
                    <div key={m.id} data-testid={`milestone-${i}`} className="relative flex flex-col items-center text-center md:px-4">
                      {/* mobile connector */}
                      {i < milestones.length - 1 && (
                        <div className="md:hidden absolute left-1/2 -translate-x-1/2 top-14 w-[2px] h-[calc(100%-1rem)] bg-neutral-200" />
                      )}
                      <div
                        className={`relative z-10 w-14 h-14 rounded-full flex items-center justify-center font-helvetica font-bold text-sm tracking-[0.15em] mb-6 ${
                          isNow
                            ? "bg-neutral-950 text-white ring-[6px] ring-neutral-100"
                            : i === 0
                            ? "bg-neutral-950 text-white"
                            : "bg-white border-2 border-neutral-300 text-neutral-400"
                        }`}
                      >
                        {/* For nodes 02 and 03, overlay an animated black fill that fades in with scroll */}
                        {i > 0 && (
                          <motion.span
                            aria-hidden="true"
                            className={`absolute inset-0 rounded-full ${isNow ? "bg-neutral-950 ring-[6px] ring-neutral-100" : "bg-neutral-950"}`}
                            style={dotMotionStyle}
                          />
                        )}
                        <span className="relative z-10">{String(i + 1).padStart(2, "0")}</span>
                      </div>
                      {isNow && (
                        <motion.span
                          className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1 bg-neutral-950 text-white font-helvetica font-bold text-[0.55rem] tracking-[0.3em] uppercase px-2.5 py-1 rounded-full whitespace-nowrap"
                          style={{ opacity: node3Opacity }}
                        >
                          You are here
                        </motion.span>
                      )}
                      <p className="font-helvetica font-bold text-[0.65rem] md:text-xs tracking-[0.32em] uppercase text-neutral-400 mb-3">
                        {isNow ? "Today" : i === 0 ? "Then" : "Recently"}
                      </p>
                      <h3 className="font-akira uppercase tracking-tight text-neutral-900 text-2xl md:text-[1.75rem] leading-[0.95] mb-4">
                        {m.label}
                      </h3>
                      <p className="font-advercase text-sm md:text-base text-neutral-600 leading-relaxed max-w-[20rem]">
                        {m.body}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CROSSROADS / PATHS */}
            <div className="text-center mb-12 md:mb-16">
              <p className="font-helvetica font-bold text-[0.65rem] md:text-xs tracking-[0.4em] uppercase text-neutral-400 mb-5">
                The Crossroads
              </p>
              <h3 className="font-akira uppercase tracking-tight text-neutral-900 text-2xl md:text-4xl leading-[0.95]">
                Four paths from here.<br />
                <span className="text-neutral-400">Only two of them keep you growing.</span>
              </h3>
            </div>

            <div data-testid="paths-grid" className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {paths.map((p) => {
                const Icon = p.icon;
                if (p.tone === "muted") {
                  return (
                    <motion.div
                      key={p.id}
                      data-testid={`path-${p.id}`}
                      className="relative rounded-[1.5rem] p-7 md:p-9 bg-neutral-100 border border-dashed border-neutral-300 text-neutral-500"
                      {...pathReveal}
                      transition={pathTransition(p.delay)}
                    >
                      <div className="flex items-start gap-4">
                        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-neutral-400" strokeWidth={2} aria-hidden="true" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-helvetica font-bold text-[0.6rem] tracking-[0.32em] uppercase text-neutral-400 mb-2">
                            {p.badge}
                          </div>
                          <h4 className="font-akira uppercase tracking-tight text-neutral-500 text-xl md:text-2xl leading-[1.05] line-through decoration-neutral-300">
                            {p.title}
                          </h4>
                          <p className="mt-4 font-advercase text-sm md:text-base leading-relaxed">
                            {p.body}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                }
                if (p.tone === "good") {
                  return (
                    <motion.div
                      key={p.id}
                      data-testid={`path-${p.id}`}
                      className="relative rounded-[1.5rem] p-7 md:p-9 bg-white border border-neutral-200 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_20px_60px_-30px_rgba(0,0,0,0.12)]"
                      {...pathReveal}
                      transition={pathTransition(p.delay)}
                    >
                      <div className="flex items-start gap-4">
                        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-neutral-950 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-white" strokeWidth={2.25} aria-hidden="true" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-helvetica font-bold text-[0.6rem] tracking-[0.32em] uppercase text-neutral-500 mb-2">
                            {p.badge}
                          </div>
                          <h4 className="font-akira uppercase tracking-tight text-neutral-900 text-xl md:text-2xl leading-[1.05]">
                            {p.title}
                          </h4>
                          <p className="mt-4 font-advercase text-sm md:text-base text-neutral-700 leading-relaxed">
                            {p.body}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                }
                // featured (contenthaus)
                return (
                  <motion.div
                    key={p.id}
                    data-testid={`path-${p.id}`}
                    className="relative rounded-[1.5rem] p-7 md:p-9 bg-neutral-950 text-white border border-neutral-900 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.5)] overflow-hidden"
                    {...pathReveal}
                    transition={pathTransition(p.delay)}
                  >
                    <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-white text-neutral-950 text-[0.6rem] font-helvetica font-bold uppercase tracking-[0.28em] px-3 py-1.5 rounded-full">
                      <Sparkles className="w-3 h-3" strokeWidth={2.5} aria-hidden="true" />
                      You are here
                    </span>
                    <div className="flex items-start gap-4 pr-28">
                      <span className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center">
                        <Icon className="w-4 h-4 text-neutral-950" strokeWidth={2.5} aria-hidden="true" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-helvetica font-bold text-[0.6rem] tracking-[0.32em] uppercase text-neutral-400 mb-2">
                          {p.badge}
                        </div>
                        <h4 className="font-akira uppercase tracking-tight text-white text-xl md:text-2xl leading-[1.05]">
                          {p.title}
                        </h4>
                        <p className="mt-4 font-advercase text-sm md:text-base text-neutral-300 leading-relaxed">
                          {p.body}
                        </p>
                        <a
                          href="#pricing"
                          data-testid="path-contenthaus-cta"
                          className="mt-6 inline-flex items-center gap-2 font-helvetica font-bold text-[0.7rem] tracking-[0.28em] uppercase text-white border-b border-white/40 pb-1 hover:border-white transition-colors"
                        >
                          See the partnership
                          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHAT YOU GET */}
        <section data-testid="offer-section" className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14 md:mb-20">
              <p data-testid="offer-eyebrow" className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
                What you get
              </p>
              <h2 data-testid="offer-title" className="font-akira uppercase tracking-tight text-neutral-900 text-3xl md:text-5xl leading-[0.95]">
                Done for you.<br />
                <span className="text-neutral-400">Not taught to you.</span>
              </h2>
            </div>

            <div
              data-testid="offer-card"
              className="bg-white border border-neutral-200 rounded-[2rem] p-8 md:p-14 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_20px_60px_-30px_rgba(0,0,0,0.12)] max-w-4xl mx-auto"
            >
              <div className="flex items-center gap-3 mb-7 md:mb-9">
                <span className="h-px flex-1 bg-neutral-200" />
                <span data-testid="every-month-label" className="font-helvetica font-bold text-[0.65rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500">
                  Every Month You Get
                </span>
                <span className="h-px flex-1 bg-neutral-200" />
              </div>
              <ul className="space-y-6 md:space-y-7">
                {offerItems.map((item, i) => (
                  <li key={i} data-testid={`offer-item-${i}`} className="flex items-start gap-5">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-neutral-950 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} aria-hidden="true" />
                    </span>
                    <span className="font-advercase text-lg md:text-xl text-neutral-900">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Manifesto / positioning card */}
              <div data-testid="positioning-card" className="mt-10 md:mt-14 relative rounded-[1.5rem] bg-neutral-950 text-white p-6 md:p-10 overflow-hidden">
                <span className="absolute top-5 right-5 md:top-6 md:right-6 inline-flex items-center gap-1.5 bg-white text-neutral-950 text-[0.6rem] md:text-[0.65rem] font-helvetica font-bold uppercase tracking-[0.28em] px-3 py-1.5 rounded-full">
                  <Sparkles className="w-3 h-3" strokeWidth={2.5} aria-hidden="true" />
                  The Promise
                </span>
                <div className="flex items-start gap-5 pr-24 md:pr-44">
                  <span className="mt-1 flex-shrink-0 w-7 h-7 rounded-full bg-white flex items-center justify-center">
                    <Check className="w-4 h-4 text-neutral-950" strokeWidth={3} aria-hidden="true" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-akira uppercase tracking-tight text-xl md:text-3xl leading-[1.05]">
                      Quality over volume.<br />
                      <span className="text-neutral-400">Content that builds your pipeline.</span>
                    </div>
                    <p className="mt-5 font-advercase text-base md:text-lg text-neutral-300 leading-relaxed">
                      We don't chase volume. We build the kind of content that compounds — the kind that keeps
                      you consistent, keeps you growing, and turns your feed into the most reliable lead
                      source you've ever owned.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className="font-helvetica font-bold text-xs tracking-[0.25em] uppercase text-white">
                        4 shoots/mo · 24 posts/mo
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section id="examples" data-testid="examples-section" className="py-20 md:py-28 border-t border-neutral-200">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14 md:mb-20">
              <p data-testid="examples-eyebrow" className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
                The work
              </p>
              <h2 data-testid="examples-title" className="font-akira uppercase tracking-tight text-neutral-900 text-3xl md:text-5xl leading-[0.95]">
                Content built to perform.<br />
                <span className="text-neutral-400">Receipts attached.</span>
              </h2>
              <p className="mt-6 font-advercase text-base md:text-lg text-neutral-500 max-w-xl mx-auto">
                A snapshot of recent Reels we produced for ContentHaus partners. Tap any one to play.
              </p>
            </div>

            <div data-testid="reels-grid" className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-6" onMouseLeave={() => setHovered(null)}>
              {reels.map((r) => (
                <ReelCard key={r.id} reel={r} />
              ))}
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section data-testid="who-section" className="py-20 md:py-28 border-t border-neutral-200">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14 md:mb-20">
              <p className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
                Who it's for
              </p>
              <h2 className="font-akira uppercase tracking-tight text-neutral-900 text-3xl md:text-5xl leading-[0.95]">
                Built for the agents<br />
                <span className="text-neutral-400">already winning.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {whoItsFor.map((w, i) => (
                <div
                  key={i}
                  data-testid={`who-card-${i}`}
                  className="bg-white border border-neutral-200 rounded-[1.5rem] p-7 md:p-8 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_20px_60px_-40px_rgba(0,0,0,0.12)] flex flex-col"
                >
                  <span className="font-helvetica font-bold text-[0.65rem] tracking-[0.32em] uppercase text-neutral-400 mb-5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-advercase text-base md:text-lg text-neutral-900 leading-snug flex-1">{w}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* REVENUE CALCULATOR */}
        <RevenueCalculator />

        {/* PRICING */}
        <section id="pricing" data-testid="pricing-section" className="py-20 md:py-32 border-t border-neutral-200">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14 md:mb-20">
              <p data-testid="pricing-eyebrow" className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
                The Partnership
              </p>
              <h2 data-testid="pricing-title" className="font-akira uppercase tracking-tight text-neutral-900 text-3xl md:text-5xl leading-[0.95]">
                Two terms.<br />
                <span className="text-neutral-400">Pick your runway.</span>
              </h2>
              <p className="mt-6 font-advercase text-base md:text-lg text-neutral-500 max-w-xl mx-auto">
                A single monthly rate for the full partnership — or a one-time listing shoot if you want to test the quality first.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto items-stretch">
              {plans.map((p) => (
                <PricingCard key={p.id} plan={p} featured={p.id === "12-months"} />
              ))}
            </div>

            {/* Single Listing Package — try-before-you-commit */}
            <div className="mt-14 md:mt-20 max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-6 md:mb-8">
                <span className="h-px flex-1 bg-neutral-200" />
                <span className="font-helvetica font-bold text-[0.65rem] md:text-xs tracking-[0.35em] uppercase text-neutral-500">
                  Not ready for six months?
                </span>
                <span className="h-px flex-1 bg-neutral-200" />
              </div>

              <div
                data-testid="single-listing-card"
                className="relative rounded-[2rem] bg-white border border-neutral-200 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_20px_60px_-30px_rgba(0,0,0,0.12)] p-8 md:p-12 grid grid-cols-1 md:grid-cols-[1.05fr_1fr] gap-8 md:gap-12 items-start"
              >
                {/* LEFT — pitch + price */}
                <div>
                  <div className="font-helvetica font-bold text-[0.6rem] md:text-[0.65rem] tracking-[0.32em] uppercase text-neutral-500 mb-4">
                    Try Before You Commit
                  </div>
                  <h3 className="font-akira uppercase tracking-tight text-neutral-900 text-2xl md:text-[2.25rem] leading-[1.0]">
                    Single Listing<br />
                    <span className="text-neutral-400">Package.</span>
                  </h3>
                  <p className="mt-5 font-advercase text-base md:text-[1.05rem] text-neutral-600 leading-relaxed">
                    One listing. One production day. Five distinct edits — the same signature
                    five-pack we use inside ContentHaus. For agents who want to feel the quality
                    on a single listing before committing to the full partnership.
                  </p>

                  <div className="mt-8 flex items-baseline gap-3">
                    <span className="font-helvetica font-bold text-2xl md:text-3xl text-neutral-400 leading-none">$</span>
                    <span className="font-helvetica font-bold text-6xl md:text-7xl text-neutral-950 tracking-tight leading-none">1,497</span>
                    <span className="font-advercase text-sm text-neutral-500 ml-1">/ listing</span>
                  </div>
                  <div className="mt-2 font-helvetica font-bold text-[0.6rem] md:text-[0.65rem] tracking-[0.28em] uppercase text-neutral-500">
                    One-time · No commitment
                  </div>

                  <a
                    data-testid="single-listing-cta"
                    href="https://book.verticalvideopro.com/contenthaus"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-neutral-950 text-white font-helvetica font-bold text-xs tracking-[0.25em] uppercase hover:bg-neutral-800 transition-colors duration-300 group"
                  >
                    Book a single shoot
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </a>
                </div>

                {/* RIGHT — deliverables */}
                <div className="md:border-l md:border-neutral-200 md:pl-12">
                  <div className="font-helvetica font-bold text-[0.6rem] md:text-[0.65rem] tracking-[0.32em] uppercase text-neutral-500 mb-5">
                    The Signature 5-Pack
                  </div>
                  <ul className="space-y-4 md:space-y-4">
                    {[
                      "Cinematic hype edit",
                      "Walking & talking tour",
                      "Sped-up walkthrough video",
                      "B-roll highlight reel · Vol. 1",
                      "B-roll highlight reel · Vol. 2",
                    ].map((item, i) => (
                      <li key={i} data-testid={`single-listing-item-${i}`} className="flex items-start gap-4">
                        <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-neutral-950 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" />
                        </span>
                        <span className="font-advercase text-base md:text-[1.05rem] text-neutral-900 leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-neutral-200">
                    <p className="font-advercase text-xs md:text-[0.8rem] text-neutral-500 italic leading-relaxed">
                      Full production day · 5 finished pieces · Delivered ready-to-post across your channels.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sample carousel of the five deliverable styles */}
              <ListingSampleCarousel />
            </div>

            <div data-testid="ready-cta" className="mt-16 md:mt-20 text-center">
              <p className="font-advercase text-base md:text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
                You don't need more effort. You need the system that turns content into clients.
              </p>
              <a
                data-testid="ready-cta-button"
                href="https://go.verticalvideopro.com/contenthaus"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-neutral-950 text-white font-helvetica font-bold text-xs tracking-[0.25em] uppercase hover:bg-neutral-800 transition-all duration-300 group"
              >
                Start the partnership
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </a>
            </div>

            <p className="mt-10 text-center font-advercase text-[0.8rem] md:text-sm text-neutral-400 tracking-wide">
              All partnerships start with a discovery call. We only onboard agents we're confident we can win for.
            </p>
          </div>
        </section>

        {/* CLOSING · One-liner under pricing */}
        <section data-testid="closing-section" className="py-16 md:py-24 border-t border-neutral-200 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2
              data-testid="closing-title"
              className="font-akira uppercase tracking-tight text-3xl md:text-5xl leading-[0.95]"
            >
              <span className="block text-neutral-300">Get more views.</span>
              <span className="block text-neutral-500">Grow your following.</span>
              <span className="block text-neutral-950">Sell more houses.</span>
            </h2>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="py-12 md:py-16 border-t border-neutral-200">
          <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-akira uppercase tracking-tight text-neutral-900 text-xl">
              Content<span className="text-neutral-400">Haus</span>
              <span className="ml-3 font-helvetica font-bold text-[0.6rem] tracking-[0.3em] uppercase text-neutral-400 align-middle">
                by Vertical Video Pro
              </span>
            </div>
            <p className="font-advercase text-xs md:text-sm text-neutral-400 tracking-wide">
              © {new Date().getFullYear()} ContentHaus — We build the brand. You close the deals.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default ContentHaus;
